import React, { useCallback } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { createEditorContentUnifiedQueryVariables } from '@confluence/editor-content-unified-query/entry-points/createEditorContentUnifiedQueryVariables.ts';
import { expValEquals } from '@confluence/feature-experiments';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isNotFoundError } from '@confluence/error-boundary/entry-points/isNotFoundError.ts';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useUpdateContentProperty } from '@confluence/update-content-property-utils/entry-points/useUpdateContentProperty.ts';
import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
const useTitleContentPropertiesForEditorQuery = graphql`
	query useTitleContentPropertiesForEditorQuery($contentId: ID, $status: [String]) {
		content(id: $contentId, status: $status) {
			nodes {
				id
				pageTitlePublished: properties(key: "page-title-property-published") {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
				pageTitleDraft: properties(key: "page-title-property-draft") {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
				metadata {
					frontend {
						coverPictureWidth
					}
				}
			}
		}
	}
`;
import { contentPropertyHelper } from './contentPropertyHelper.ts';
import type {
	useTitleContentPropertiesForEditorQuery$data,
	useTitleContentPropertiesForEditorQuery$variables,
} from './__generated__/useTitleContentPropertiesForEditorQuery.graphql.ts';
import { useTitleContentPropertiesFragment } from './useTitleContentPropertiesFragment.ts';
import {
	TitleAlignmentType,
	defaultPropertyValue,
	COVERPICTUREWIDTH,
} from './contentTopperConstants.ts';

type UseTitleContentPropertiesForEditorProps = {
	contentId: string;
	propertyKey?: string;
	isDelete?: boolean;
	loadTitleContentProperties?: boolean;
	updateAlignmentState?: (alignment?: TitleAlignmentType) => void;
	updateCoverPictureWidthState?: (coverPictureWidth: COVERPICTUREWIDTH) => void;
	contentType: 'page' | 'blogpost' | null;
	flags?: FlagsStateContainer;
	updateIsLoadingComplete?: (isLoadingComplete: boolean) => void;
};

export const getPageTitleDraftTitlePropertyContent = (
	queryData: useTitleContentPropertiesForEditorQuery$data | null,
):
	| {
			readonly id: string | null | undefined;
			readonly key: string | null | undefined;
			readonly value: string | null | undefined;
			readonly version:
				| {
						readonly number: number | null | undefined;
				  }
				| null
				| undefined;
	  }
	| null
	| undefined =>
	queryData?.content?.nodes?.[0]?.pageTitleDraft?.nodes?.find(
		(node) => node?.key === 'page-title-property-draft',
	);

const i18n = defineMessages({
	errorFlagTitle: {
		id: 'content-topper.page-title.alignment.error.title',
		defaultMessage: 'We couldn’t update the title alignment',
		description:
			"Title for error flag when a user tries to change title alignment and there's an error",
	},
	errorFlagDescription: {
		id: 'content-topper.page-title.alignment.error.description',
		defaultMessage: 'Try again, or refresh the page.',
		description:
			"Description for error flag when a user tries to change title alignment and there's an error",
	},
});

export const useTitleContentPropertiesForEditor = ({
	contentId,
	propertyKey = 'page-title-property-draft',
	isDelete = false,
	loadTitleContentProperties = false,
	updateAlignmentState,
	updateCoverPictureWidthState,
	contentType,
	flags,
	updateIsLoadingComplete,
}: UseTitleContentPropertiesForEditorProps): {
	isUpdatingAlignment: boolean;
	updatePageTitleProperty: (propertyValue: any) => Promise<void>;
	// exported for testing purposes
	onCompleted: (response: any) => void;
	isTitleAlignmentLoading: boolean;
	isTitleCenteredInitial: boolean;
	coverImageWidthCached: COVERPICTUREWIDTH;
} => {
	const isInitialLoadComplete = React.useRef(false);
	const isTitleCenteredInitial = React.useRef(false);

	const handleError = useCallback(
		(error: any, suppressErrors: boolean) => {
			const isUnauthOrNotFoundError = isUnauthorizedError(error) || isNotFoundError(error);
			if (!isUnauthOrNotFoundError) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.MIGRATIONS_TOOLING,
				});
			}
			markErrorAsHandled(error);
			if (!suppressErrors) {
				void flags?.showErrorFlag({
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagDescription} />,
				});
			}
		},
		[flags],
	);

	const getAlignmentFromContentProperty = (response: any): TitleAlignmentType => {
		const titlePropertyContent = getPageTitleDraftTitlePropertyContent(response);
		if (titlePropertyContent?.value) {
			try {
				return (
					contentPropertyHelper(titlePropertyContent)?.titleLayoutAlignment ||
					defaultPropertyValue.titleLayoutAlignment
				);
			} catch (e) {
				handleError(e, false);
				return defaultPropertyValue.titleLayoutAlignment;
			}
		} else {
			return defaultPropertyValue.titleLayoutAlignment;
		}
	};

	const onCompleted = (response: any): void => {
		const coverPictureWidth: COVERPICTUREWIDTH =
			response.content?.nodes?.[0]?.metadata?.frontend?.coverPictureWidth || COVERPICTUREWIDTH.FULL;
		void updateAlignmentState?.(getAlignmentFromContentProperty(response));
		void updateCoverPictureWidthState?.(coverPictureWidth);
	};

	// Reading the fragment needs no request, so every caller reads it.
	const titleContentPropertiesFragmentResult = useTitleContentPropertiesFragment({ contentId });
	// Stands in for the skipped query's onError; stable so a new handleError does not report again.
	const handleFragmentError = useStableCallback(handleError);
	const {
		error: titleContentPropertiesFieldError,
		isLoading: isTitleContentPropertiesFragmentLoading,
	} = titleContentPropertiesFragmentResult;
	// onError fires once per document, not on refetch, whose field errors also reach the fragment.
	const fieldErrorReportedContentIdRef = React.useRef<string | null>(null);
	React.useEffect(() => {
		// Only callers that run the query handle its error, so it is reported as often as before.
		if (
			titleContentPropertiesFieldError &&
			!isTitleContentPropertiesFragmentLoading &&
			fieldErrorReportedContentIdRef.current !== contentId &&
			(loadTitleContentProperties || Boolean(window.__SSR_RENDERED__))
		) {
			fieldErrorReportedContentIdRef.current = contentId;
			handleFragmentError(titleContentPropertiesFieldError, true);
		}
	}, [
		titleContentPropertiesFieldError,
		isTitleContentPropertiesFragmentLoading,
		contentId,
		loadTitleContentProperties,
		handleFragmentError,
	]);
	const titleContentPropertiesQueryResult = useQueryInterop<
		useTitleContentPropertiesForEditorQuery$data,
		useTitleContentPropertiesForEditorQuery$variables
	>(useTitleContentPropertiesForEditorQuery, {
		variables: expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)
			? createEditorContentUnifiedQueryVariables({ contentId })
			: {
					contentId,
					status: ['current', 'draft', 'archived'],
				},
		fetchPolicy: window.__SSR_RENDERED__ ? 'cache-first' : 'cache-and-network', // cache-and-network is required for: edit cover width, transition out of edit, transition back to edit for same page (for Live Docs and Classic pages)
		onError: (error) => {
			handleError(error, true);
		},
		skip:
			titleContentPropertiesFragmentResult.isProvided ||
			!(loadTitleContentProperties || Boolean(window.__SSR_RENDERED__)),
		notifyOnNetworkStatusChange: true,
	});
	const { data } = titleContentPropertiesFragmentResult.isProvided
		? titleContentPropertiesFragmentResult
		: titleContentPropertiesQueryResult;
	// The skipped query's refetch writes the records the fragment reads, so it updates the fragment.
	const { networkStatus, refetch: refetchProperties } = titleContentPropertiesQueryResult;
	const isTitleAlignmentLoading = networkStatus === NetworkStatus.loading && !data;
	const refetchLoading = networkStatus === NetworkStatus.refetch;
	const coverImageWidthCached = data?.content?.nodes?.[0]?.metadata?.frontend?.coverPictureWidth;

	isTitleCenteredInitial.current =
		(Boolean(window.__SSR_RENDERED__) || !isTitleAlignmentLoading) &&
		getAlignmentFromContentProperty(data) === TitleAlignmentType.CENTER;
	if (!isInitialLoadComplete.current && loadTitleContentProperties && data) {
		isInitialLoadComplete.current = true;
		onCompleted(data);
		updateIsLoadingComplete?.(true);
	}

	const {
		updateContentProperty,
		updateContentPropertyResult: { loading: isUpdatingAlignment } = {},
	} = useUpdateContentProperty({
		contentId,
		contentType: contentType || 'page',
	});

	const onPageTitlePropertyUpdatedSubscriptionData = useCallback(
		(_eventName: string, response: any) => {
			const { pageTitlePropertyUpdated } = response || {};
			try {
				const value = JSON.parse(pageTitlePropertyUpdated?.value);
				void updateAlignmentState?.(value?.titleLayoutAlignment);
			} catch (error) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
				markErrorAsHandled(error);
			}
		},
		[updateAlignmentState],
	);

	useSubscriptionEvent({
		contentId,
		contentType: contentType || 'page',
		delta: 'pageTitlePropertyUpdated',
		onDataReceived: onPageTitlePropertyUpdatedSubscriptionData,
		subscriberId: 'useTitleContentPropertiesForEditor',
	});

	const updatePageTitleProperty = useCallback(
		async (propertyValue: any): Promise<void> => {
			try {
				const getVersionNumber = (data: any) =>
					data?.content?.nodes?.[0]?.pageTitleDraft?.nodes?.[0]?.version?.number || 0;
				const versionData = await refetchProperties();
				const versionNumber = getVersionNumber(versionData.data);
				const newVersionNumber = versionNumber ? versionNumber + 1 : 1;

				await updateContentProperty({
					propertyKey,
					propertyValue,
					isDelete,
					hasExistingProperty: versionNumber > 0,
					shouldOptimisticResponse: true,
					options: {
						update: () => {
							try {
								const cacheData = readQueryInterop({
									query: useTitleContentPropertiesForEditorQuery,
									variables: expValEquals(
										'confluence_editor_content_unified_query',
										'isEnabled',
										true,
									)
										? createEditorContentUnifiedQueryVariables({ contentId })
										: {
												contentId,
												status: ['current', 'draft', 'archived'],
											},
								});
								(cacheData as any).content!.nodes![0]!.pageTitleDraft!.nodes![0] = {
									...(cacheData as any).content!.nodes![0]!.pageTitleDraft!.nodes![0],
									id: contentId,
									key: propertyKey,
									value: JSON.stringify(propertyValue),
									version: {
										number: newVersionNumber,
										__typename: 'Version',
									},
								};

								writeQueryInterop({
									query: useTitleContentPropertiesForEditorQuery,
									variables: expValEquals(
										'confluence_editor_content_unified_query',
										'isEnabled',
										true,
									)
										? createEditorContentUnifiedQueryVariables({ contentId })
										: {
												contentId,
												status: ['current', 'draft', 'archived'],
											},
									data: cacheData,
								});
							} catch (error) {
								throw new Error(
									` failed to write to cache while updating ${propertyKey} content property after user updated page title${error.message}`,
								);
							}
						},
					},
				});
			} catch (err) {
				handleError(err, false);
			}
		},
		[propertyKey, contentId, isDelete, updateContentProperty, refetchProperties, handleError],
	);

	return {
		isUpdatingAlignment: isUpdatingAlignment || refetchLoading,
		updatePageTitleProperty,
		// exported for testing purposes
		onCompleted,
		isTitleAlignmentLoading,
		isTitleCenteredInitial: isTitleCenteredInitial.current,
		coverImageWidthCached: coverImageWidthCached as COVERPICTUREWIDTH,
	};
};
